// Les formulaires officiels des reçus fiscaux (D-124), remplis PAR-DESSUS, sans toucher à leur mise en page :
//  - 2041-RD, cerfa n° 11580*05 (particuliers, bénévoles) : sans champs ; l'app écrit sur ses pointillés et coche ses
//    cases, aux positions relevées au point près dans le PDF publié (scripts/cerfa/calibrer.py → cerfa-calibrage.json,
//    vérifiées par src/lib/cerfa-pdf.test.ts) ;
//  - 2041-MEC-SD, cerfa n° 16216*03 (entreprises) : ses vrais champs ; des noms se répètent d'une page à l'autre
//    (CAC2 à CAC5 : des cases d'organismes en page 1, la forme des versements en page 2) : chaque case est désignée par
//    sa page, son nom ET son état « coché », jamais par son nom seul.
// Une édition = un tableau. Une nouvelle édition parue : nouveau PDF dans public/cerfa/, nouveau calibrage, nouveau tableau.
// Les cases suivent la notice : bénéficiaire « Œuvre ou organisme d'intérêt général » › « Association loi 1901 » ;
// particulier : article 200 du CGI ; frais d'un bénévole : nature « Frais engagés par les bénévoles, dont ils renoncent
// expressément au remboursement » ; don en argent : « Numéraire » et le mode de versement ; « Forme du don » : celle que
// le club a choisie dans ses réglages, sinon rien (la notice ne tranche pas pour un abandon de frais).
import { eurosInWords } from './montant-lettres';
import { amountDigits, ddmmyyyy, type Donor, type GiftForm, type GiftMode, type Org } from './recus';

/** Coordonnées relevées : en points, origine en HAUT à gauche (PyMuPDF). pdf-lib compte depuis le bas : y = hauteur − y. */
type Line = { page: number; x: number; base: number; right: number; size: number };
type Box = { page: number; cx: number; cy: number; half: number };

export const RD_11580_05 = {
  form: '2041-RD' as const, edition: '11580*05', file: '2041-rd_4298.pdf',
  sha256: '2433262f8362490140eb940db9b7126cf319b958ed18b85a34a0fc1c010573a1', height: 842,
  // Le texte se pose 0,8 pt au-dessus des pointillés (ligne de base des points : « base »).
  lines: {
    org_name: { page: 0, x: 36.5, base: 178.92, right: 557.39, size: 10 },
    org_ident: { page: 0, x: 197.5, base: 189.36, right: 557.66, size: 10 },
    org_num: { page: 0, x: 51.5, base: 218.28, right: 93.7, size: 9.5 },
    org_street: { page: 0, x: 135.5, base: 218.28, right: 556.79, size: 9.5 },
    org_zip: { page: 0, x: 98, base: 232.32, right: 156.26, size: 9.5 },
    org_city: { page: 0, x: 232, base: 232.32, right: 557.42, size: 9.5 },
    org_country: { page: 0, x: 65, base: 244.44, right: 182.54, size: 9.5 },
    org_purpose: { page: 0, x: 72, base: 256.8, right: 557.99, size: 9.5 },
    org_purpose2: { page: 0, x: 36.5, base: 267.12, right: 558.86, size: 9.5 },
    donor_last: { page: 1, x: 65, base: 250.44, right: 263.83, size: 10 },
    donor_first: { page: 1, x: 353.5, base: 251.4, right: 547.43, size: 10 },
    donor_num: { page: 1, x: 52, base: 281.16, right: 83.02, size: 9.5 },
    donor_street: { page: 1, x: 136, base: 281.16, right: 546.02, size: 9.5 },
    donor_zip: { page: 1, x: 99, base: 295.44, right: 134.78, size: 9.5 },
    donor_city: { page: 1, x: 232, base: 295.44, right: 546.74, size: 9.5 },
    donor_country: { page: 1, x: 66.5, base: 310.44, right: 196.79, size: 9.5 },
    words: { page: 1, x: 355.5, base: 363.0, right: 550.06, size: 9 },
  } satisfies Record<string, Line>,
  // Les cadres : numéro d'ordre (centré), montant (aligné à droite), « Date et signature ».
  number: { page: 0, box: [418.2, 111.0, 567.24, 128.16] as const, base: 123.6, size: 11 },
  amount: { page: 1, box: [42.48, 351.36, 155.88, 368.4] as const, right: 150.5, base: 364.0, size: 11 },
  // « ……/……/…… » : chaque morceau de pointillés reçoit le jour, le mois, l'année.
  giftDate: { page: 1, base: 383.64, size: 9.5, slots: [[180.48, 204.34], [207.6, 233.86], [237.12, 263.43]] as const },
  sign: {
    page: 1, box: [303.24, 613.08, 521.64, 662.04] as const,
    date: { base: 641.4, size: 10, slots: [[308.88, 324.46], [328.44, 343.9], [347.88, 363.34]] as const, extra: 5 },   // l'année : jusqu'à 368,3
    who: { x: 308.5, base: 657.6, right: 519, size: 7.5 },           // « Nathalie Durand, présidente »
    image: [369, 615.5, 519, 649] as const,                          // la signature, à droite de la date
  },
  // L'aperçu de « Signer le reçu » : le bas de la page 2, rendu depuis le PDF publié (scripts/cerfa/apercus.py).
  preview: { page: 1, file: 'apercu-2041-rd_4298-p2.png', crop: [28, 500, 568, 668] as const },
  // Les cases (centre du carré ou du rond tel qu'il est dessiné ; demi-côté de la coche).
  boxes: {
    interest: { page: 0, cx: 38.0, cy: 396.37, half: 2.2 },          // Œuvre ou organisme d'intérêt général
    loi1901: { page: 0, cx: 67.63, cy: 345.5, half: 1.9 },           // › Association loi 1901 (un rond)
    art200: { page: 1, cx: 103.37, cy: 445.25, half: 2.2 },
    form_acte_authentique: { page: 1, cx: 38.0, cy: 482.25, half: 2.2 },
    form_seing_prive: { page: 1, cx: 153.75, cy: 482.25, half: 2.2 },
    form_don_manuel: { page: 1, cx: 294.37, cy: 482.25, half: 2.2 },
    form_autres: { page: 1, cx: 486.87, cy: 482.25, half: 2.2 },
    numeraire: { page: 1, cx: 38.0, cy: 519.13, half: 2.2 },
    frais: { page: 1, cx: 38.0, cy: 539.13, half: 2.2 },
    mode_especes: { page: 1, cx: 38.0, cy: 587.0, half: 2.2 },
    mode_cheque: { page: 1, cx: 153.75, cy: 587.0, half: 2.2 },
    mode_virement: { page: 1, cx: 294.37, cy: 587.0, half: 2.2 },
  } satisfies Record<string, Box>,
};

export const MEC_16216_03 = {
  form: '2041-MEC-SD' as const, edition: '16216*03', file: '2041-mec-sd_4811.pdf',
  sha256: '7a08ebce32f153d560015dd8a8e9c3d44683a52cef9cf657e8328d60ef5d0728', height: 841.89,
  // Largeur des champs de texte « en toutes lettres » (le reste passe sur la ligne suivante, b17 et b20) et de la dénomination.
  widths: { a2: 349.8, b16: 274.8, b19: 194.6 },
  // La case d'un champ : sa page, son nom, son état « coché » (unique sur la page pour ce nom).
  checks: {
    interest: { page: 0, name: 'CAC1', on: '1' },
    loi1901: { page: 0, name: 'CAC0', on: '1' },
    mode_especes: { page: 1, name: 'CAC2', on: 'A' },
    mode_cheque: { page: 1, name: 'CAC3', on: 'A' },
    mode_virement: { page: 1, name: 'CAC4', on: 'A' },
    mode_autre: { page: 1, name: 'CAC5', on: 'A' },
  },
  // L'aperçu (bas de la page 2) et la place des champs qui s'y voient (rectangles des widgets, relevés par le calibrage).
  preview: { page: 1, file: 'apercu-2041-mec-sd_4811-p2.png', crop: [28, 495, 568, 692] as const },
  fieldRects: { b18: [30.46, 537.93, 124.61, 553.68], b19: [368.02, 550.75, 562.61, 564.72], b21: [463.34, 576.86, 544.84, 587.81], b27: [347.62, 618.75, 429.11, 634.5] } as Record<string, readonly number[]>,
  // Le cadre « Date et signature » (page 2) : sous le champ « Le … », la signature, puis le nom et la qualité.
  sign: { page: 1, cell: [328.6, 614.75, 566.7, 686.15] as const, image: [336, 637, 560, 671.5] as const, who: { x: 334.5, base: 682.6, right: 563, size: 8 } },
};

export interface CerfaData {
  number: string;                 // « 2026-008 »
  org: Org;
  donor: Donor;
  amountCents: number;
  giftDate: string;               // « 2026-10-29 » : date du don (frais : la déclaration d'abandon)
  nature: 'numeraire' | 'frais';
  mode: GiftMode | null;
  signDate: string;               // le jour de la signature
  signer: { name: string; quality: string } | null;
}

export interface PlanText { page: number; key: string; text: string; x: number; y: number; size: number; minSize: number; maxWidth: number; align: 'left' | 'center' | 'right' }
export interface PlanCheck { page: number; key: string; cx: number; cy: number; half: number }
export interface PlanImage { page: number; x: number; y: number; w: number; h: number }
export interface RdPlan { kind: 'rd'; texts: PlanText[]; checks: PlanCheck[]; signature: PlanImage }
export interface MecPlan { kind: 'mec'; fields: Record<string, string>; checks: { page: number; name: string; on: string; key: string }[]; texts: PlanText[]; signature: PlanImage }

const LIFT = 0.8;   // au-dessus des pointillés
const toPdf = (h: number, yTop: number) => Math.round((h - yTop) * 100) / 100;
/** Le texte d'une ligne relevée, en coordonnées pdf-lib. */
function onLine(h: number, key: string, l: Line, text: string, minSize = 6): PlanText {
  return { page: l.page, key, text, x: l.x, y: toPdf(h, l.base - LIFT), size: l.size, minSize, maxWidth: l.right - l.x, align: 'left' };
}
/** Une date « 30/10/2026 » posée morceau par morceau sur « ……/……/…… ». */
/** L'année, plus longue que ses pointillés, part du début du dernier morceau et peut le dépasser de `extra` points. */
function inSlots(h: number, key: string, page: number, base: number, size: number, slots: readonly (readonly [number, number])[], iso: string, extra = 0): PlanText[] {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return [d!, m!, y!].map((t, i) => {
    const [x0, x1] = slots[i]!;
    const year = i === 2 && extra > 0;
    return { page, key: `${key}_${['jour', 'mois', 'annee'][i]}`, text: t, x: year ? x0 : (x0 + x1) / 2, y: toPdf(h, base - LIFT), size, minSize: size - 2,
      maxWidth: year ? x1 - x0 + extra : x1 - x0, align: year ? 'left' as const : 'center' as const };
  });
}
/** Le reste d'un texte trop long pour une ligne : coupé au dernier espace qui tient. */
export function splitToWidth(text: string, width: number, measure: (t: string) => number): [string, string] {
  if (measure(text) <= width) return [text, ''];
  const words = text.split(' ');
  let head = '';
  for (let i = 0; i < words.length; i++) {
    const next = head ? `${head} ${words[i]}` : words[i]!;
    if (measure(next) > width) return [head || next, words.slice(head ? i : i + 1).join(' ')];
    head = next;
  }
  return [head, ''];
}

const formKey = (f: GiftForm | null) => (f ? `form_${f}` as const : null);
const modeKey = (m: GiftMode | null) => (m === 'especes' ? 'mode_especes' : m === 'cheque' ? 'mode_cheque' : m === 'virement' ? 'mode_virement' : null);

/** Ce que l'app pose sur le 2041-RD : textes, cases cochées, cadre de la signature. */
export function planRd(d: CerfaData, measure: (text: string, size: number) => number): RdPlan {
  const T = RD_11580_05, H = T.height, L = T.lines;
  const texts: PlanText[] = [];
  texts.push({ page: T.number.page, key: 'number', text: d.number, x: (T.number.box[0] + T.number.box[2]) / 2, y: toPdf(H, T.number.base),
    size: T.number.size, minSize: 8, maxWidth: T.number.box[2] - T.number.box[0] - 6, align: 'center' });
  const o = d.org;
  const add = (key: keyof typeof L, text: string | null | undefined) => { if (text && text.trim()) texts.push(onLine(H, key, L[key], text.trim())); };
  add('org_name', o.name);
  add('org_ident', o.ident);
  add('org_num', o.num);
  add('org_street', o.street);
  add('org_zip', o.zip);
  add('org_city', o.city);
  add('org_country', o.country ?? 'France');
  if (o.purpose) {
    const [a, b] = splitToWidth(o.purpose.trim(), L.org_purpose.right - L.org_purpose.x, t => measure(t, L.org_purpose.size));
    add('org_purpose', a);
    add('org_purpose2', b);
  }
  const p = d.donor;
  if (p.kind === 'company') add('donor_last', p.company);   // une entreprise sur le 2041-RD n'arrive pas : le 2041-MEC-SD la prend
  else { add('donor_last', p.last_name?.toUpperCase()); add('donor_first', p.first_names); }
  add('donor_num', p.num);
  add('donor_street', p.street);
  add('donor_zip', p.zip);
  add('donor_city', p.city);
  add('donor_country', p.country ?? 'France');
  texts.push({ page: T.amount.page, key: 'amount', text: amountDigits(d.amountCents), x: T.amount.right, y: toPdf(H, T.amount.base),
    size: T.amount.size, minSize: 8, maxWidth: T.amount.box[2] - T.amount.box[0] - 8, align: 'right' });
  texts.push({ ...onLine(H, 'words', L.words, eurosInWords(d.amountCents), 5.5) });
  texts.push(...inSlots(H, 'gift_date', T.giftDate.page, T.giftDate.base, T.giftDate.size, T.giftDate.slots, d.giftDate));
  texts.push(...inSlots(H, 'sign_date', T.sign.page, T.sign.date.base, T.sign.date.size, T.sign.date.slots, d.signDate, T.sign.date.extra));
  if (d.signer) {
    texts.push({ page: T.sign.page, key: 'signer', text: `${d.signer.name}, ${d.signer.quality}`, x: T.sign.who.x, y: toPdf(H, T.sign.who.base),
      size: T.sign.who.size, minSize: 5.5, maxWidth: T.sign.who.right - T.sign.who.x, align: 'left' });
  }
  const B = T.boxes;
  const checks: PlanCheck[] = [];
  const tick = (key: keyof typeof B) => { const b = B[key]; checks.push({ page: b.page, key, cx: b.cx, cy: toPdf(H, b.cy), half: b.half }); };
  tick('interest');
  tick('loi1901');
  tick('art200');
  const form = formKey(d.nature === 'frais' ? o.form_expenses : o.form_cash);
  if (form) tick(form);
  if (d.nature === 'frais') tick('frais');
  else {
    tick('numeraire');
    const m = modeKey(d.mode);
    if (m) tick(m);
  }
  const im = T.sign.image;
  return { kind: 'rd', texts, checks, signature: { page: T.sign.page, x: im[0], y: toPdf(H, im[3]), w: im[2] - im[0], h: im[3] - im[1] } };
}

/** Ce que l'app met dans les champs du 2041-MEC-SD, les cases à cocher (page, nom, état), le cadre de la signature. */
export function planMec(d: CerfaData, measure: (text: string, size: number) => number): MecPlan {
  const T = MEC_16216_03, H = T.height, o = d.org, p = d.donor;
  const f: Record<string, string> = {};
  const put = (k: string, v: string | null | undefined) => { if (v && v.trim()) f[k] = v.trim(); };
  put('a1', d.number);
  const [n1, n2] = splitToWidth(o.name, T.widths.a2, t => measure(t, 10));
  put('a2', n1); put('a3', n2);
  put('a4', o.ident);
  put('a5', o.num); put('a6', o.street); put('a7', o.zip); put('a8', o.city); put('a9', o.country ?? 'France');
  put('a10', o.purpose);
  put('b4', p.company); put('b5', p.legal_form); put('b6', p.siren?.replace(/\s/g, ''));   // le champ prend 9 caractères
  put('b7', p.num); put('b8', p.street); put('b9', p.zip); put('b10', p.city);
  const words = eurosInWords(d.amountCents);
  put('b15', amountDigits(d.amountCents));
  const [w1, w2] = splitToWidth(words, T.widths.b16, t => measure(t, 10));
  put('b16', w1); put('b17', w2);
  put('b18', amountDigits(d.amountCents));
  const [x1, x2] = splitToWidth(words, T.widths.b19, t => measure(t, 10));
  put('b19', x1); put('b20', x2);
  put('b21', ddmmyyyy(d.giftDate));
  put('b27', ddmmyyyy(d.signDate));
  const C = T.checks;
  const checks = [{ ...C.interest, key: 'interest' }, { ...C.loi1901, key: 'loi1901' }];
  const m = d.mode === 'especes' ? 'mode_especes' : d.mode === 'cheque' ? 'mode_cheque' : d.mode === 'virement' ? 'mode_virement' : d.mode === 'autre' ? 'mode_autre' : null;
  if (m) checks.push({ ...C[m], key: m });
  const texts: PlanText[] = d.signer ? [{ page: T.sign.page, key: 'signer', text: `${d.signer.name}, ${d.signer.quality}`, x: T.sign.who.x,
    y: toPdf(H, T.sign.who.base), size: T.sign.who.size, minSize: 5.5, maxWidth: T.sign.who.right - T.sign.who.x, align: 'left' }] : [];
  const im = T.sign.image;
  return { kind: 'mec', fields: f, checks, texts, signature: { page: T.sign.page, x: im[0], y: toPdf(H, im[3]), w: im[2] - im[0], h: im[3] - im[1] } };
}

/** Le formulaire d'un donateur : une entreprise → 2041-MEC-SD ; une personne, un bénévole → 2041-RD. */
export const formFor = (donor: Donor) => (donor.kind === 'company' ? MEC_16216_03 : RD_11580_05);
